import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Save, FileText, AlignLeft, FolderOpen, File, Send } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Input from '../../common/Input';
import RecipientPicker from './RecipientPicker';
import { updateReport, getReportById } from '../../../services/reportServiceSwitch';
import { getGroups } from '../../../services/groupServiceSwitch';
import { getEmails } from '../../../services/emailServiceSwitch';
import { getAllSftpSites } from '../../../services/sftpServiceSwitch';
import { getAllPickupFolders } from '../../../services/pickupServiceSwitch';
import { getCategories } from '../../../services/categoryService';
import './ReportManagement.css';

export default function EditReportModal({ isOpen, onClose, report, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '', description: '', category: '', filePath: '',
    deliveryMethod: 'SIGNED_URL',
    emailSubject: '', emailBody: '', urlLifespanDays: '1',
    groupIds: [], emailIds: [], sftpSiteIds: [], pickupFolderIds: []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const [allEmails, setAllEmails] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [allSftpSites, setAllSftpSites] = useState([]);
  const [allPickupFolders, setAllPickupFolders] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (report && isOpen) loadReportDetails();
  }, [report, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReportDetails = async () => {
    try {
      setLoadingData(true);
      const [fullReport, emailRes, groupRes, sftpRes, pickupRes, cats] = await Promise.all([
        getReportById(report.id).catch(() => report),
        getEmails({ page: 1, limit: 750 }).catch(() => ({ data: [] })),
        getGroups({ page: 1, limit: 200 }).catch(() => ({ data: [] })),
        getAllSftpSites().catch(() => null),
        getAllPickupFolders().catch(() => []),
        getCategories().catch(() => [])
      ]);
      setAllEmails(emailRes.data || []);
      setAllGroups(groupRes.data || []);
      setAllSftpSites(sftpRes ? sftpRes.filter(s => s.isActive) : []);
      setAllPickupFolders(pickupRes || []);
      setCategories(cats);
      setFormData({
        name: fullReport.name || '',
        description: fullReport.description || '',
        category: fullReport.category || '',
        filePath: fullReport.filePath || '',
        deliveryMethod: fullReport.deliveryMethod || 'SIGNED_URL',
        emailSubject: fullReport.emailSubject || '',
        emailBody: fullReport.emailBody || '',
        urlLifespanDays: fullReport.urlLifespanDays ? String(fullReport.urlLifespanDays) : '1',
        groupIds: (fullReport.groups || []).map(g => g.id),
        emailIds: (fullReport.emails || []).map(e => e.id),
        sftpSiteIds: (fullReport.sftpSites || []).map(s => s.id),
        pickupFolderIds: (fullReport.pickupFolders || []).map(f => f.id)
      });
    } catch {
      setFormData({
        name: report.name || '', description: report.description || '',
        category: report.category || '', filePath: report.filePath || '',
        deliveryMethod: report.deliveryMethod || 'SIGNED_URL',
        emailSubject: '', emailBody: '', urlLifespanDays: '1',
        groupIds: [], emailIds: [], sftpSiteIds: [], pickupFolderIds: []
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleDeliveryMethodChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev, deliveryMethod: value,
      groupIds: value === 'SFTP' || value === 'SFTP_PICKUP' ? [] : prev.groupIds,
      emailIds: value === 'SFTP' || value === 'SFTP_PICKUP' ? [] : prev.emailIds,
      sftpSiteIds: value === 'SIGNED_URL' || value === 'SFTP_PICKUP' ? [] : prev.sftpSiteIds,
      pickupFolderIds: value === 'SFTP_PICKUP' ? prev.pickupFolderIds : []
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Report name is required';
    else if (formData.name.trim().length < 3) newErrors.name = 'At least 3 characters';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    else if (formData.description.trim().length < 10) newErrors.description = 'At least 10 characters';
    if (!formData.category) newErrors.category = 'Category is required';
    if (formData.deliveryMethod === 'SFTP' && formData.sftpSiteIds.length === 0) {
      newErrors.sftpSiteIds = 'Select at least one SFTP site';
    }
    if (formData.deliveryMethod === 'SFTP_PICKUP' && formData.pickupFolderIds.length === 0) {
      newErrors.pickupFolderIds = 'Select at least one pickup folder';
    }
    if (formData.deliveryMethod === 'SIGNED_URL' && formData.groupIds.length === 0 && formData.emailIds.length === 0) {
      newErrors.recipients = 'Select at least one email or group';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      await updateReport(report.id, formData);
      if (onSuccess) onSuccess('Report updated successfully');
      onClose();
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to update report' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { if (!loading) { setErrors({}); onClose(); } };

  if (!report) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Report" size="lg" closeOnBackdrop={!loading}
      footer={
        <div className="modal-actions">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={loading} icon={<Save size={18} />}>Save Changes</Button>
        </div>
      }
    >
      {loadingData ? (
        <div className="loading-placeholder">Loading report details...</div>
      ) : (
        <form onSubmit={handleSubmit} className="report-form">
          {errors.submit && <div className="form-error" style={{marginBottom: '1rem'}}>{errors.submit}</div>}

          <div className="form-group">
            <label htmlFor="edit-name" className="form-label"><FileText size={16} /> Report Name</label>
            <Input id="edit-name" name="name" type="text" value={formData.name} onChange={handleChange} placeholder="Monthly Sales Report" error={errors.name} disabled={loading} />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="edit-description" className="form-label"><AlignLeft size={16} /> Description</label>
            <textarea id="edit-description" name="description" value={formData.description} onChange={handleChange} placeholder="Describe what this report contains..." className={`form-textarea ${errors.description ? 'error' : ''}`} disabled={loading} rows={3} />
            {errors.description && <span className="error-text">{errors.description}</span>}
          </div>

          <div className="form-row">
            <div className="form-group form-group-half">
              <label htmlFor="edit-category" className="form-label"><FolderOpen size={16} /> Category</label>
              <select id="edit-category" name="category" value={formData.category} onChange={handleChange} className={`form-select ${errors.category ? 'error' : ''}`} disabled={loading}>
                <option value="">Select a category</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group form-group-half">
              <label htmlFor="edit-deliveryMethod" className="form-label"><Send size={16} /> Delivery Method</label>
              <select id="edit-deliveryMethod" name="deliveryMethod" value={formData.deliveryMethod} onChange={handleDeliveryMethodChange} className="form-select" disabled={loading}>
                <option value="SIGNED_URL">Signed URL (Email/Groups)</option>
                <option value="SFTP">SFTP Delivery</option>
                <option value="SFTP_PICKUP">SFTP Pickup Folder</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="edit-filePath" className="form-label"><File size={16} /> BOXI File Path</label>
            <Input id="edit-filePath" name="filePath" type="text" value={formData.filePath} onChange={handleChange} placeholder="/Reports/Sales/Monthly_Report.rpt" error={errors.filePath} disabled={loading} />
            {errors.filePath && <span className="error-text">{errors.filePath}</span>}
          </div>

          {/* Email Customization Fields — only relevant for Signed URL delivery */}
          {formData.deliveryMethod === 'SIGNED_URL' && (
            <>
              <div className="form-group">
                <label htmlFor="edit-emailSubject" className="form-label">Email Subject (Optional)</label>
                <Input id="edit-emailSubject" name="emailSubject" type="text" value={formData.emailSubject} onChange={handleChange} placeholder="Leave blank for default: 'BOXI Report Available'" disabled={loading} />
              </div>

              <div className="form-group">
                <label htmlFor="edit-emailBody" className="form-label">Email Body (Optional)</label>
                <textarea id="edit-emailBody" name="emailBody" value={formData.emailBody} onChange={handleChange} placeholder="Leave blank for default: 'Your BOXI report is ready for download.'" className="form-textarea" disabled={loading} rows={3} />
              </div>

              <div className="form-group">
                <label htmlFor="edit-urlLifespanDays" className="form-label">URL Lifespan (Days)</label>
                <select id="edit-urlLifespanDays" name="urlLifespanDays" value={formData.urlLifespanDays} onChange={handleChange} className="form-select" disabled={loading}>
                  <option value="1">1 day</option>
                  <option value="2">2 days</option>
                  <option value="3">3 days</option>
                  <option value="4">4 days</option>
                  <option value="5">5 days</option>
                  <option value="6">6 days</option>
                  <option value="7">7 days</option>
                  <option value="10">10 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                </select>
              </div>
            </>
          )}

          {/* Recipient Picker — shown for SIGNED_URL and SFTP */}
          {formData.deliveryMethod !== 'SFTP_PICKUP' && (
            <div className="form-group">
              <label className="form-label">
                {formData.deliveryMethod === 'SIGNED_URL' ? 'Recipients' : 'SFTP Destinations'}
              </label>
              <RecipientPicker
                availableEmails={allEmails}
                availableGroups={allGroups}
                availableSftpSites={allSftpSites}
                selectedEmailIds={formData.emailIds}
                selectedGroupIds={formData.groupIds}
                selectedSftpSiteIds={formData.sftpSiteIds}
                onAddEmail={(id) => setFormData(prev => ({ ...prev, emailIds: [...prev.emailIds, id] }))}
                onRemoveEmail={(id) => setFormData(prev => ({ ...prev, emailIds: prev.emailIds.filter(x => x !== id) }))}
                onAddGroup={(id) => setFormData(prev => ({ ...prev, groupIds: [...prev.groupIds, id] }))}
                onRemoveGroup={(id) => setFormData(prev => ({ ...prev, groupIds: prev.groupIds.filter(x => x !== id) }))}
                onAddSftpSite={(id) => setFormData(prev => ({ ...prev, sftpSiteIds: [...prev.sftpSiteIds, id] }))}
                onRemoveSftpSite={(id) => setFormData(prev => ({ ...prev, sftpSiteIds: prev.sftpSiteIds.filter(x => x !== id) }))}
                deliveryMethod={formData.deliveryMethod}
                disabled={loading}
              />
              {errors.recipients && <span className="error-text">{errors.recipients}</span>}
              {errors.sftpSiteIds && <span className="error-text">{errors.sftpSiteIds}</span>}
            </div>
          )}

          {/* Pickup Folder Picker — shown for SFTP_PICKUP */}
          {formData.deliveryMethod === 'SFTP_PICKUP' && (
            <div className="form-group">
              <label className="form-label">Pickup Folders</label>
              <div className="folder-checkbox-list" style={{ maxHeight: '200px', overflowY: 'auto', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: 'var(--glass-bg)' }}>
                {allPickupFolders.length === 0 && <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>No active pickup folders available</span>}
                {allPickupFolders.map(f => (
                  <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.pickupFolderIds.includes(f.id)}
                      onChange={() => {
                        setFormData(prev => ({
                          ...prev,
                          pickupFolderIds: prev.pickupFolderIds.includes(f.id)
                            ? prev.pickupFolderIds.filter(x => x !== f.id)
                            : [...prev.pickupFolderIds, f.id]
                        }));
                      }}
                      disabled={loading}
                    />
                    {f.name}
                  </label>
                ))}
              </div>
              {errors.pickupFolderIds && <span className="error-text">{errors.pickupFolderIds}</span>}
            </div>
          )}

        </form>
      )}
    </Modal>
  );
}

EditReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    category: PropTypes.string,
    filePath: PropTypes.string,
    deliveryMethod: PropTypes.string
  }),
  onSuccess: PropTypes.func
};
